/**
 * @fileoverview RadioGroupRenderer component for the Storyteller web application.
 * Renders a group of radio button options with styling and accessibility features.
 * Supports both string and numeric values, error states, and i18n translations.
 *
 * @example
 * // Basic usage
 * <RadioGroupRenderer
 *   schema={{
 *     type: 'string',
 *     items: {
 *       oneOf: [
 *         { const: 'option1', title: 'Option 1' },
 *         { const: 'option2', title: 'Option 2' }
 *       ]
 *     }
 *   }}
 *   path="selectedOption"
 *   handleChange={(path, value) => console.log(path, value)}
 *   required={true}
 * />
 */


import { ControlProps, JsonSchema4, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsEnumProps } from '@jsonforms/react'
import { VanillaRendererProps, withVanillaControlProps } from '@jsonforms/vanilla-renderers'
import { useTranslation } from 'react-i18next'
import css from './RadioGroupRenderer.module.css'

/**
 * @function classNames
 * @description Helper function to join class names with spaces.
 * @param {...string} classes - The class names to join.
 * @returns {string} The joined class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function RadioGroup
 * @description Component for rendering a radio group.
 * @param {ControlProps & VanillaRendererProps} props - The props for the RadioGroup component.
 * @returns {JSX.Element} The RadioGroup component.
 */
function RadioGroup({
    schema,
    uischema,
    handleChange,
    data,
    errors,
    path,
    required,
}: ControlProps & VanillaRendererProps) {
    const { t } = useTranslation()
    const hasError = errors.length > 0

    return (
        <div>
            <div className={classNames(css.container, hasError && css.error)}>
                {(schema.items as JsonSchema4).oneOf?.map((value) => (
                    <label
                        htmlFor={`${path}-${value.const}`}
                        className={css.item}
                        key={`option-${value.const}`}
                    >
                        <div className={css.radio}>
                            <input
                                name={path}
                                id={`${path}-${value.const}`}
                                type="radio"
                                value={value.const}
                                className={css.input}
                                required={required}
                                onChange={() => {
                                    if (schema.type === 'number') {
                                        handleChange(path, Number(value.const))
                                    } else {
                                        handleChange(path, value.const)
                                    }
                                }}
                            />
                            <label
                                className={css['radio-label']}
                                htmlFor={`${path}-${value.const}`}
                            >
                                <button
                                    onClick={() => {
                                        if (schema.type === 'number') {
                                            handleChange(path, Number(value.const))
                                        } else {
                                            handleChange(path, value.const)
                                        }
                                    }}
                                    className={
                                        data === value.const
                                            ? css['radio-selected']
                                            : css['radio-unselected']
                                    }
                                />
                            </label>
                        </div>
                        <div className={css.label}>
                            <label htmlFor={`${path}-${value.const}`}>
                                {t(`options.${uischema.options?.name}.${value.title}`)}
                            </label>
                        </div>
                    </label>
                ))}
            </div>
            {hasError && <div className={css.errorMessage}>{errors}</div>}
        </div>
    )
}

export const RadioGroupTester = rankWith(3, uiTypeIs('RadioGroup'))

export default withVanillaControlProps(withJsonFormsEnumProps(RadioGroup))
